import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { CreateVehicleModelRequest, VehicleModel } from "@/types";
import { createInitialFormData } from "@/utils/formHelpers";
import { useFormState } from "@/hooks/useFormState";
import { createCrudHooks } from "@/hooks/crudHooksFactory";
import i18n from "@/i18n/config";

export function useVehicleModels(params?: {
  search?: string;
  maker?: string;
  year?: number;
  yearRange?: string;
  limit?: number;
  offset?: number;
  orderBy?: "createdAt" | "year" | "maker" | "model";
  orderDirection?: "asc" | "desc";
}) {
  return useQuery({
    queryKey: ["vehicleModels", params],
    queryFn: () => apiClient.getVehicleModels(params),
  });
}

const vehicleModelCrudHooks = createCrudHooks({
  create: {
    mutationFn: (vehicleModelData: CreateVehicleModelRequest) =>
      apiClient.createVehicleModel(vehicleModelData),
    cacheActions: [{ type: "invalidate", key: ["vehicleModels"] }],
  },
  update: {
    mutationFn: ({
      id,
      ...vehicleModelData
    }: { id: string } & Partial<CreateVehicleModelRequest>) =>
      apiClient.updateVehicleModel(id, vehicleModelData),
    cacheActions: [
      { type: "invalidate", key: ["vehicleModels"] },
      {
        type: "invalidate",
        key: (updatedVehicleModel) => ["vehicleModels", updatedVehicleModel.id],
      },
    ],
  },
  delete: {
    mutationFn: (id: string) => apiClient.deleteVehicleModel(id),
    cacheActions: [{ type: "invalidate", key: ["vehicleModels"] }],
  },
});

export const useCreateVehicleModel = vehicleModelCrudHooks.useCreate;
export const useUpdateVehicleModel = vehicleModelCrudHooks.useUpdate;
export const useDeleteVehicleModel = vehicleModelCrudHooks.useDelete;

// Validation logic extracted as a pure function for stable reference
function validateVehicleModelData(
  data: Partial<CreateVehicleModelRequest>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const currentYear = new Date().getFullYear();

  if (!data.year || isNaN(data.year)) {
    errors.year = i18n.t("pages:vehicles.modelValidation.yearRequired");
  } else if (data.year < 1900 || data.year > currentYear + 5) {
    errors.year = i18n.t("pages:vehicles.modelValidation.yearRange", {
      max: currentYear + 5,
    });
  }

  if (!data.maker?.trim()) {
    errors.maker = i18n.t("pages:vehicles.modelValidation.makerRequired");
  } else if (data.maker.length > 255) {
    errors.maker = i18n.t("pages:vehicles.modelValidation.makerTooLong");
  }

  if (!data.model?.trim()) {
    errors.model = i18n.t("pages:vehicles.modelValidation.modelRequired");
  } else if (data.model.length > 255) {
    errors.model = i18n.t("pages:vehicles.modelValidation.modelTooLong");
  }

  if (data.description && data.description.length > 1000) {
    errors.description = i18n.t(
      "pages:vehicles.modelValidation.descriptionTooLong",
    );
  }

  return errors;
}

// Custom hook for managing vehicle model form state
export const useVehicleModelForm = (initialVehicleModel?: VehicleModel) => {
  const getInitialVehicleModelData = useCallback(
    () =>
      createInitialFormData(initialVehicleModel, {
        year: undefined as number | undefined,
        maker: "",
        model: "",
        description: "",
      }),
    [initialVehicleModel],
  );

  const {
    data: vehicleModelData,
    errors,
    validate,
    updateField,
    resetForm,
    setData: setVehicleModelData,
  } = useFormState(getInitialVehicleModelData, validateVehicleModelData);

  return {
    vehicleModelData,
    errors,
    validate,
    updateField,
    resetForm,
    setVehicleModelData,
  };
};
